import { forwardRef, useMemo } from "react";
import styles from "./styles.module.css";
import { useBundles } from "../../entities";
import {
  BinaryAsBooleanValues,
  IService,
  LanguageKeys,
  ProductType,
  VehicleType,
} from "../../entities/interface";
import { getSortableServiceList, sortBundles } from "../../helpers";
import { BundleBox } from "../boxes/BundleBox";
import { Spinner } from "../spinner";

interface IBundleProps {
  regionId: number;
  locationId: number;
  vehicleType: VehicleType;
  serviceId: string | null;
  bundleId: string | null;
  services: IService[];
  servicesLoading: boolean;
  language: LanguageKeys;
  ids: number[];
}

export const ServicesList = forwardRef<HTMLDivElement, IBundleProps>(
  (props, ref) => {
    const {
      regionId,
      locationId,
      vehicleType,
      serviceId,
      bundleId,
      services,
      servicesLoading,
      language,
      ids,
    } = props;
    const { data: bundles, isLoading: bundlesLoading } = useBundles({
      regionId: props.regionId,
      isActive: BinaryAsBooleanValues.True,
      isVisible: BinaryAsBooleanValues.True,
    });

    const visibleServices = services;
    const bundlesData = bundles?.result;
    const regionBundles = getSortableServiceList(
      [
        ...(bundlesData || []).map((b) => ({
          ...b,
          productType: ProductType.Bundle,
        })),
      ],
      visibleServices || []
    ).filter((bun) => bun.vehicleType === vehicleType);

    const sortedBundles = useMemo(() => {
      return regionBundles
        .filter(
          (bundle) => bundle.languageCode === language || !bundle.languageCode
        )
        .sort((a, b) => sortBundles(a, b, LanguageKeys.Swedish, ids));
    }, [ids, language, regionBundles]);

    const regionServices = visibleServices
      ?.filter((s) =>
        s?.associationsIds?.ownLocations?.includes(props?.locationId)
      )
      .filter((s) => s.isVisible)
      .sort((a, b) => a.priceWithVat - b.priceWithVat);

    const sortedServices = useMemo(() => {
      return regionServices?.filter(
        (ser) => ser.languageCode === language || !ser.languageCode
      );
    }, [language, regionServices]);

    const isServicesBundles =
      locationId &&
      regionId &&
      sortedBundles.length <= 0 &&
      sortedServices &&
      sortedServices.length <= 0;

    const isLoading =
      locationId && regionId && (bundlesLoading || servicesLoading);
    const serviceLng = language === LanguageKeys.English ? "en" : "sw";

    return isLoading ? (
      <Spinner />
    ) : (
      <div className={styles.servicesConteiner}>
        {isServicesBundles ? (
          <p>Det finns inga tjänster tillgängliga på denna plats</p>
        ) : (
          ""
        )}
        {locationId > 0 && regionId > 0 && (
          <>
            {sortedBundles && sortedBundles?.length > 0 && (
              <ul className={styles.bundleBoxesContainer}>
                {sortedBundles?.map((bundle) => {
                  return (
                    <li className={styles.bundleContainer} key={bundle?.id}>
                      <BundleBox
                        bundleId={Number(bundleId)}
                        bundle={bundle}
                        lng={language}
                        locationId={locationId}
                      />
                    </li>
                  );
                })}
              </ul>
            )}
            {sortedServices && sortedServices?.length > 0 && (
              <div className={styles.serviceContainer}>
                <h3 className={styles.serviceTitle}>
                  {language === LanguageKeys.English ? "Lessons" : "Lektioner"}
                </h3>
                <ul className={styles.serviceBoxesContainer}>
                  {sortedServices?.map((service) => {
                    return (
                      <li
                        className={
                          service.id === Number(serviceId)
                            ? styles.hightLighted
                            : ""
                        }
                        key={service?.id}
                      >
                        <span className={styles.serviceName}>
                          {service.name[serviceLng]}{" "}
                        </span>
                        <span className={styles.servicePrice}>
                          {service.priceWithVat / 100}:-{" "}
                        </span>
                        <a
                          target="_blank"
                          rel="noreferrer"
                          href={`https://booking.mydrivingacademy.com/widget?vehicleType=${service.vehicleType}&productType=Service&serviceId=${service.id}&regionId=${regionId}&locationId=${locationId}`}
                        >
                          {language === LanguageKeys.English
                            ? "Buy now"
                            : "Boka nu"}{" "}
                        </a>{" "}
                      </li>
                    );
                  })}
                </ul>
              </div>
            )}
          </>
        )}
      </div>
    );
  }
);
